// NP1 — /ai-risk-index — Standalone, searchable, filterable AI Impact Index.
// SEO + LinkedIn shareable + working-professional hook.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, AlertTriangle, Share2, ArrowRight, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { RISK_LEVELS, DEPARTMENTS, RISK_ROLES } from '../data/aiRiskIndex';
import { useRoleCount } from '../hooks/useRoleCount';
import useCanSeeCounts from '../hooks/useCanSeeCounts';

const RISK_FILTERS = ['All', 'High', 'Medium', 'Low'];

export default function AIRiskIndexPage() {
  const [q, setQ] = useState('');
  const [risk, setRisk] = useState('All');
  const [dept, setDept] = useState('All');
  const [copied, setCopied] = useState(false);
  const roleCount = useRoleCount();
  const rc = roleCount ?? 260;
  const canSeeCounts = useCanSeeCounts();

  const rows = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return RISK_ROLES.filter((r) => {
      if (risk !== 'All' && r.risk !== risk) return false;
      if (dept !== 'All' && r.department !== dept) return false;
      if (qLower && !`${r.role} ${r.department}`.toLowerCase().includes(qLower)) return false;
      return true;
    });
  }, [q, risk, dept]);

  const shareText = (canSeeCounts
      ? `I just checked how AI is impacting ${rc}+ careers in India. Here's what I found on LearnixTa's AI Risk Index → `
      : `I just checked how AI is impacting careers in India. Here's what I found on LearnixTa's AI Risk Index → `) +
    (typeof window !== 'undefined' ? `${window.location.origin}/ai-risk-index` : 'learnixta.com/ai-risk-index');

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard blocked — silent */
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="ai-risk-index-page">
      <AnnouncementBar />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-12 px-4 md:px-6 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-semibold tracking-widest uppercase mb-3 inline-flex items-center gap-2" style={{ color: '#1D9E75' }}>
            <AlertTriangle size={14} /> LearnixTa · AI Impact Index
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-[1.1] tracking-tight max-w-4xl" data-testid="ai-risk-index-h1">
            How AI is changing every career in India — <span className="text-teal-300">role by role.</span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 mt-5 max-w-3xl leading-relaxed">
            {canSeeCounts
              ? <>We mapped AI disruption risk across <span className="text-white font-semibold">{rc}+ roles</span> so Indian students and professionals don&apos;t have to guess. Updated as AI capabilities evolve.</>
              : <>We mapped AI disruption risk across <span className="text-white font-semibold">every major career</span> so Indian students and professionals don&apos;t have to guess. Updated as AI capabilities evolve.</>}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Last reviewed: June 2026
          </div>
        </div>
      </section>

      {/* Risk-level legend */}
      <section className="px-4 md:px-6 -mt-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ai-risk-index-legend">
          {['Low', 'Medium', 'High'].map((lvl) => {
            const r = RISK_LEVELS[lvl];
            return (
              <div key={lvl} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-start gap-3">
                <span
                  className="text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shrink-0 inline-flex items-center gap-1.5"
                  style={{ background: r.pillBg, color: r.pillText }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.dot }} />
                  {lvl}
                </span>
                <p className="text-xs text-gray-600 leading-relaxed">{r.meaning}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-3 mb-6">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search your role or department…"
                className="pl-10 h-12"
                data-testid="ai-risk-index-search"
              />
            </div>
            <select
              value={risk}
              onChange={(e) => setRisk(e.target.value)}
              className="h-12 border-2 border-gray-200 hover:border-teal-400 focus:border-teal-500 rounded-xl px-3 text-sm font-medium text-gray-800 bg-white outline-none"
              data-testid="ai-risk-index-filter-risk"
            >
              {RISK_FILTERS.map((r) => (
                <option key={r} value={r}>{r === 'All' ? 'All risk levels' : `${r} risk`}</option>
              ))}
            </select>
            <select
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              className="h-12 border-2 border-gray-200 hover:border-teal-400 focus:border-teal-500 rounded-xl px-3 text-sm font-medium text-gray-800 bg-white outline-none"
              data-testid="ai-risk-index-filter-dept"
            >
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>{d === 'All' ? 'All departments' : d}</option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-500 mb-3" data-testid="ai-risk-index-count">
            Showing <span className="font-semibold text-gray-900">{rows.length}</span> of {RISK_ROLES.length} roles
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid="ai-risk-index-table-full">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                <tr>
                  <th className="text-left px-4 md:px-6 py-4 sticky left-0 bg-gray-50">Role</th>
                  <th className="text-left px-4 py-4">Department</th>
                  <th className="text-left px-4 py-4">AI Risk</th>
                  <th className="text-left px-4 py-4">What AI Automates</th>
                  <th className="text-left px-4 py-4">What Stays Human</th>
                  <th className="text-left px-4 py-4">How to Stay Relevant</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-14 text-gray-500">
                      No roles match those filters. Try widening the search.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const lvl = RISK_LEVELS[r.risk];
                    return (
                      <tr key={i} className="border-t border-gray-100 hover:bg-teal-50/30 transition-colors">
                        <td className="px-4 md:px-6 py-4 font-semibold text-gray-900 sticky left-0 bg-white">{r.role}</td>
                        <td className="px-4 py-4 text-gray-500">{r.department}</td>
                        <td className="px-4 py-4">
                          <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                            style={{ background: lvl.pillBg, color: lvl.pillText }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: lvl.dot }} />
                            {r.risk}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-gray-700 max-w-[260px]">{r.automates}</td>
                        <td className="px-4 py-4 text-gray-700 max-w-[260px]">{r.staysHuman}</td>
                        <td className="px-4 py-4 text-gray-700 max-w-[260px]">{r.stayRelevant}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* CTA + share */}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button asChild className="btn-primary h-12 px-6">
              <Link to="/explore/ai" data-testid="ai-risk-index-cta">
                See your full AI risk report + how to upskill <ArrowRight size={16} className="ml-1.5" />
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={copyShare}
              className="h-12 px-5 border-2 border-gray-300 hover:border-teal-500 hover:bg-teal-50"
              data-testid="ai-risk-index-share-btn"
            >
              {copied ? <><CheckCircle2 size={16} className="mr-2 text-emerald-600" /> Copied!</> : <><Share2 size={16} className="mr-2" /> Share on LinkedIn / WhatsApp</>}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-5 max-w-2xl">
            <span className="font-semibold text-gray-700">Note:</span> AI risk levels are LearnixTa's editorial assessment, based on current state of foundation models and Indian job-market signals. Reviewed quarterly.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
